import React, { useEffect } from 'react';

import { DocNode } from '@atlaskit/adf-schema';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { ReactRenderer } from '@atlaskit/renderer';

import { buildProfilecardProvider } from 'src/components/editor/providers/profilecard-provider';
import {
  rendererTTRSeverityTracking,
  unsupportedContentLevelsTracking,
} from 'src/constants/renderer-tracking';
import { useIntl } from 'src/hooks/intl';
import { usePortalContainer } from 'src/hooks/use-portal-container';

import { providerFactory } from '../editor/provider-factory';

import { toJson } from './bb-json-transformer';
import { htmlTransformer } from './bb-renderer-transformer';

const defaultTransformer = 'html';
const transformerMapping = {
  html: htmlTransformer,
};

export type FabricHtmlRendererProps = {
  format: 'html';
  src: string;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const FabricRenderer: React.FC<FabricHtmlRendererProps> = (
  props: FabricHtmlRendererProps
) => {
  const { src, format } = props;
  const intl = useIntl();
  const portal = usePortalContainer();

  useEffect(() => {
    providerFactory.setProvider(
      'profilecardProvider',
      buildProfilecardProvider({ intl })
    );
    // this intentionally only runs on the initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transformer =
    transformerMapping[format] || transformerMapping[defaultTransformer];
  const parsedHtml = transformer.parse(src);
  const document = toJson(parsedHtml) as DocNode;

  return (
    <ReactRenderer
      appearance="comment"
      analyticsEventSeverityTracking={rendererTTRSeverityTracking}
      unsupportedContentLevelsTracking={unsupportedContentLevelsTracking}
      useSpecBasedValidator
      schema={defaultSchema}
      // @ts-ignore our version of renderer requires a different version of editor-common
      dataProviders={providerFactory}
      document={document}
      portal={portal}
      allowCopyToClipboard
    />
  );
};
